"use client";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoMdArrowDropdown } from "react-icons/io";
import Checkbox from "./Checkbox";

export default function CustomSelect({
  label = "Select option",
  options = [],
  value,
  onSelect,
  multiple = false,
  children,
  className = "",
  isSearching = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  // ✅ handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ handle selection (single or multiple)
  const handleSelect = (option) => {
    if (multiple) {
      const current = Array.isArray(value) ? [...value] : [];
      const exists = current.includes(option);
      const updated = exists
        ? current.filter((v) => v !== option)
        : [...current, option];
      onSelect?.(updated);
      // don’t close in multiple mode
    } else {
      onSelect?.(option);
      setIsOpen(false);
    }
  };

  // ✅ Display text for button
  const displayValue = multiple
    ? value?.length > 0
      ? value[0]
      : label
    : value || label;

  return (
    <div ref={selectRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full flex  items-center  text-[#242424] text-sm rounded-[3px]  transition-colors  relative ${
          isSearching
            ? "bg-white justify-between py-1 px-2.5 border border-[#BABABA]/50"
            : "bg-[#D7EFFF] justify-center min-w-[120px] px-8 py-2"
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className={` ${isSearching ? "" : "absolute right-3"}`}
        >
          <IoMdArrowDropdown color="#242424" size={20} />
        </motion.span>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute left-0 mt-3 w-full bg-white rounded-[10px] shadow-[0_9px_12px_0_rgba(0,_0,_0,_0.15)] overflow-hidden z-50"
          >
            <div className="max-h-52 overflow-auto scrollbar-hide py-2.5 flex flex-col gap-1">
              {options.length > 0 ? (
                options.map((opt, i) => {
                  const isSelected = multiple
                    ? value?.includes(opt)
                    : value === opt;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSelect(opt)}
                      className={`text-[14px] text-[#242424] flex items-center gap-3 w-full text-left  transition ${
                        isSelected ? "bg-[#EDF0FB]" : ""
                      }  ${multiple ? "justify-start" : "justify-center"} ${
                        isSearching ? "p-1" : "px-4 py-2 hover:bg-[#EDF0FB]"
                      }`}
                    >
                      {multiple && (
                        <Checkbox
                          checked={isSelected}
                          borderColor="border-[#242424]"
                          color="#242424"
                          onChange={() => handleSelect(opt)}
                        />
                      )}

                      {opt}
                    </button>
                  );
                })
              ) : (
                <div className="p-2">{children}</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
